import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  CalendarDays,
  Check,
  Copy,
  Info,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import SchedulerHeader from '@/components/SchedulerHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  addDaysInSingapore,
  formatDateToString,
  getCurrentSingaporeDate,
  parseDateString,
} from '@/lib/dateUtils';
import { getPlatoDoctorName } from '@/lib/platoDoctors';

// ---------------------------------------------------------------------------
// Daily List
//
// One day's REAL Plato appointments grouped per doctor, each card with a
// "Copy for WhatsApp" button. Replaces the front desk manually compiling
// tomorrow's per-doctor appointment list for WhatsApp every evening.
// Data comes from the plato-day-schedule Edge Function (waitlist entries
// are excluded server-side). Defaults to TOMORROW in Singapore time.
// ---------------------------------------------------------------------------

interface DayScheduleAppointment {
  time: string;
  endtime: string;
  title: string;
  all_day: boolean;
}

interface DayScheduleDoctor {
  code: string;
  appointments: DayScheduleAppointment[];
}

interface DayScheduleResponse {
  success: boolean;
  error?: string;
  date?: string;
  doctors?: DayScheduleDoctor[];
  window_start?: string | null;
  window_end?: string | null;
}

interface DayScheduleResult {
  doctors: DayScheduleDoctor[];
  windowStart: string | null;
  windowEnd: string | null;
}

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const fetchDaySchedule = async (date: string): Promise<DayScheduleResult> => {
  const { data, error } = await supabase.functions.invoke('plato-day-schedule', {
    body: { date },
  });

  if (error) {
    throw new Error(`Plato day schedule error: ${error.message}`);
  }

  const payload = data as DayScheduleResponse | null;
  if (!payload?.success) {
    throw new Error(payload?.error || 'Failed to load the day schedule from Plato');
  }

  return {
    doctors: payload.doctors ?? [],
    windowStart: payload.window_start ?? null,
    windowEnd: payload.window_end ?? null,
  };
};

const formatHeadingDate = (date: string): string => {
  try {
    return format(parseDateString(date), 'EEE d MMM yyyy');
  } catch {
    return date;
  }
};

const formatAppointmentTime = (appointment: DayScheduleAppointment): string => {
  if (appointment.all_day || !appointment.time) {
    return 'All day';
  }
  return appointment.time;
};

// Plain-text WhatsApp block for one doctor, e.g.
// "Appointments for Dr Richard Ang - Thu 12 Jun 2026:\n1. 09:00 TAN AH KOW\n...\nTotal: N"
const buildDoctorMessage = (doctor: DayScheduleDoctor, date: string): string => {
  const heading = `Appointments for ${getPlatoDoctorName(doctor.code)} - ${formatHeadingDate(date)}:`;
  const lines = doctor.appointments.map((appointment, index) =>
    `${index + 1}. ${formatAppointmentTime(appointment)} ${appointment.title}`.trimEnd()
  );
  return [heading, ...lines, `Total: ${doctor.appointments.length}`].join('\n');
};

const DailyList: React.FC = () => {
  const navigate = useNavigate();
  const { canAccessPatients } = useAuth();
  const { toast } = useToast();

  // SchedulerHeader is shared with the rest of the dashboard. The date
  // navigator is not meaningful on this page (it has its own date picker),
  // so we hold a local stub date and ignore navigation callbacks.
  const [headerDate, setHeaderDate] = useState(getCurrentSingaporeDate());

  // Default to TOMORROW in Singapore time - the front desk compiles the
  // next day's list every evening.
  const [date, setDate] = useState(() =>
    formatDateToString(addDaysInSingapore(getCurrentSingaporeDate(), 1))
  );
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const hasAccess = canAccessPatients();
  const isValidDate = DATE_PATTERN.test(date);

  const {
    data: schedule,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['plato-day-schedule', date],
    queryFn: () => fetchDaySchedule(date),
    enabled: hasAccess && isValidDate,
  });
  const doctors = schedule?.doctors ?? [];

  const handleCopy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      window.setTimeout(() => {
        setCopiedKey((current) => (current === key ? null : current));
      }, 2000);
    } catch {
      toast({
        title: 'Could not copy to clipboard',
        description: 'Select the text manually, or try again.',
        variant: 'destructive',
      });
    }
  };

  const handleCopyAll = () => {
    const text = doctors
      .map((doctor) => buildDoctorMessage(doctor, date))
      .join('\n\n');
    void handleCopy('__all__', text);
  };

  const totalAppointments = doctors.reduce(
    (sum, doctor) => sum + doctor.appointments.length,
    0
  );

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <SchedulerHeader
          currentView="patients"
          onViewChange={(view) => navigate(`/${view}`)}
          currentDate={headerDate}
          onNavigate={() => undefined}
          onDateChange={setHeaderDate}
          copiedItemsCount={0}
        />
        <div className="max-w-3xl mx-auto px-4 py-12">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>No access to Daily List</AlertTitle>
            <AlertDescription>
              The Daily List uses patient data, so it follows the same access
              rules as the Patients tab. If you should have access, ask an
              admin to update your role.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SchedulerHeader
        currentView="patients"
        onViewChange={(view) => navigate(`/${view}`)}
        currentDate={headerDate}
        onNavigate={() => undefined}
        onDateChange={setHeaderDate}
        copiedItemsCount={0}
      />

      <div className="w-full px-2 py-3 sm:px-4 sm:py-6">
        <div className="w-full space-y-4">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-primary" />
                <h1 className="text-2xl font-semibold text-gray-900">Daily List</h1>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Per-doctor appointment list from Plato, ready to paste into WhatsApp.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Flow fix 2026-07-03: one-tap Today / Tomorrow. The page defaults
                  to tomorrow (evening compile workflow) but daytime users kept
                  landing on an unexplained empty date. */}
              {(() => {
                const todayStr = formatDateToString(getCurrentSingaporeDate());
                const tomorrowStr = formatDateToString(
                  addDaysInSingapore(getCurrentSingaporeDate(), 1)
                );
                return (
                  <div className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
                    <Button
                      variant={date === todayStr ? 'default' : 'ghost'}
                      size="sm"
                      className="h-7"
                      onClick={() => setDate(todayStr)}
                    >
                      Today
                    </Button>
                    <Button
                      variant={date === tomorrowStr ? 'default' : 'ghost'}
                      size="sm"
                      className="h-7"
                      onClick={() => setDate(tomorrowStr)}
                    >
                      Tomorrow
                    </Button>
                  </div>
                );
              })()}
              <Input
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                className="h-9 w-44"
                aria-label="Schedule date"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={!isValidDate || isLoading || isRefetching}
              >
                {isRefetching ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1" />
                )}
                Load
              </Button>
              <Button
                size="sm"
                onClick={handleCopyAll}
                disabled={isLoading || isError || doctors.length === 0}
              >
                {copiedKey === '__all__' ? (
                  <Check className="h-4 w-4 mr-1" />
                ) : (
                  <Copy className="h-4 w-4 mr-1" />
                )}
                {copiedKey === '__all__' ? 'Copied' : 'Copy all doctors'}
              </Button>
            </div>
          </div>

          {/* Invalid date */}
          {!isValidDate && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Pick a date</AlertTitle>
              <AlertDescription>
                Choose a date to load that day's appointments.
              </AlertDescription>
            </Alert>
          )}

          {/* Error state */}
          {isValidDate && isError && (
            <Alert variant="destructive">
              <AlertTitle>Could not load appointments</AlertTitle>
              <AlertDescription>
                {error instanceof Error
                  ? error.message
                  : 'Something went wrong reading the Plato schedule.'}{' '}
                Try Load again, or contact an admin if it persists.
              </AlertDescription>
            </Alert>
          )}

          {/* Loading state */}
          {isValidDate && isLoading && (
            <Card>
              <CardContent className="py-12 flex items-center justify-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading appointments for {formatHeadingDate(date)}...
              </CardContent>
            </Card>
          )}

          {/* Empty state - explains WHY it can be empty (Plato only exposes a
              small rolling window of upcoming bookings, so tomorrow often fills
              up later in the day) instead of a bare "no appointments". */}
          {isValidDate && !isLoading && !isError && doctors.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground space-y-2">
                <p className="font-medium text-slate-700">
                  No appointments loaded for {formatHeadingDate(date)}.
                </p>
                <p className="text-sm">
                  Plato only shows a small rolling window of upcoming bookings, so
                  tomorrow's list usually fills up by the evening. Try Today, or
                  press Load again later.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Doctor cards */}
          {isValidDate && !isLoading && !isError && doctors.length > 0 && (
            <>
              <p className="text-sm text-muted-foreground">
                {formatHeadingDate(date)} &middot; {doctors.length}{' '}
                {doctors.length === 1 ? 'doctor' : 'doctors'} &middot;{' '}
                {totalAppointments}{' '}
                {totalAppointments === 1 ? 'appointment' : 'appointments'}
                {schedule?.windowStart && schedule?.windowEnd ? (
                  <span className="block text-xs">
                    From Plato's booking window {schedule.windowStart} to{' '}
                    {schedule.windowEnd} - cross-check against Plato for the
                    full day until the window size is confirmed.
                  </span>
                ) : null}
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                {doctors.map((doctor) => (
                  <Card key={doctor.code} className="shadow-sm">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <CardTitle className="text-base font-semibold">
                            {getPlatoDoctorName(doctor.code)}
                          </CardTitle>
                          <Badge variant="secondary" className="mt-1 text-xs">
                            {doctor.appointments.length}{' '}
                            {doctor.appointments.length === 1
                              ? 'appointment'
                              : 'appointments'}
                          </Badge>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                          onClick={() =>
                            handleCopy(doctor.code, buildDoctorMessage(doctor, date))
                          }
                        >
                          {copiedKey === doctor.code ? (
                            <Check className="h-4 w-4 mr-1" />
                          ) : (
                            <Copy className="h-4 w-4 mr-1" />
                          )}
                          {copiedKey === doctor.code ? 'Copied' : 'Copy for WhatsApp'}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="divide-y divide-gray-100">
                        {doctor.appointments.map((appointment, index) => (
                          <div
                            key={`${appointment.time}-${index}`}
                            className="py-2 text-sm flex items-baseline gap-2"
                          >
                            <span className="font-medium text-gray-900 tabular-nums shrink-0">
                              {formatAppointmentTime(appointment)}
                            </span>
                            <span className="text-gray-400 shrink-0">-</span>
                            <span className="text-gray-700 min-w-0 break-words">
                              {appointment.title || 'Untitled appointment'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyList;
